<template>
  <div v-if="show" class="hc-mask" @click.self="close">
    <!-- 体检进行中 -->
    <div v-if="phase === 'checking'" class="hc-scan" @click.stop>
      <div class="hc-scan-card">
        <div class="hc-scan-ring">
          <svg viewBox="0 0 24 24" width="36" height="36" fill="none" stroke="#0466dd" stroke-width="1.9"
            stroke-linecap="round" stroke-linejoin="round" class="hc-heart">
            <path d="M3 12h4l2 5 4-10 2 5h6" />
          </svg>
        </div>
        <div class="hc-scan-title">正在为项目做体检</div>
        <div class="hc-scan-sub">
          {{ scan_loading_names.length ? '正在检测：' + scan_loading_names.join('、') : '检测完成，正在生成报告…' }}
        </div>

        <div class="hc-scan-bar">
          <span :style="{ width: scan_percent + '%' }"></span>
        </div>
        <div class="hc-scan-percent">{{ Math.round(scan_percent) }}%</div>

        <div class="hc-scan-steps">
          <div v-for="d in report.dimensions" :key="d.key" class="hc-scan-step"
            :class="{ done: step_status[d.key] === 'done', active: step_status[d.key] === 'loading' }">
            <span class="hc-scan-dot">
              <Icon v-if="step_status[d.key] === 'done'" type="md-checkmark" size="12" color="#fff" />
              <span v-else-if="step_status[d.key] === 'loading'" class="hc-scan-spin"></span>
            </span>
            <span class="hc-scan-step-name">{{ d.name }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 体检报告 -->
    <div v-else class="hc-panel">
      <!-- 头部 -->
      <div class="hc-hd">
        <div class="hc-hd-l">
          <div class="hc-hd-badge">
            <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#fff" stroke-width="1.9"
              stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 12h4l2 5 4-10 2 5h6" />
            </svg>
          </div>
          <div>
            <div class="hc-hd-title">项目体检报告 <span class="hc-ver">BETA</span></div>
            <div class="hc-hd-sub">
              <span>项目：<b>{{ report.project_name || '—' }}</b></span>
              <span>体检时间：<b>{{ report.update_time }}</b></span>
            </div>
          </div>
        </div>
        <div class="hc-hd-r">
          <div class="hc-btn" @click="recheck">
            <Icon type="md-refresh" size="14" /> 重新体检
          </div>
          <div class="hc-btn hc-btn-solid" @click="export_report">
            <Icon type="md-download" size="14" /> 导出报告
          </div>
          <Icon type="md-close" class="hc-close" @click="close" />
        </div>
      </div>

      <div class="hc-body">
        <div class="hc-top-row">
          <!-- 总分 -->
          <div class="hc-hero" :style="{
            background: score_theme.bg,
            borderColor: score_theme.border,
            boxShadow: '0 12px 36px ' + score_theme.glow,
            '--c': score_theme.color,
            '--glow': score_theme.glow
          }">
            <div class="hc-gauge">
              <svg width="140" height="140" viewBox="0 0 140 140">
                <defs>
                  <linearGradient id="hcGaugeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" :stop-color="score_theme.color" />
                    <stop offset="100%" :stop-color="score_theme.color2" />
                  </linearGradient>
                </defs>
                <circle class="hc-track" cx="70" cy="70" r="56" />
                <circle class="hc-bar" cx="70" cy="70" r="56" :stroke-dasharray="gaugeC"
                  :stroke-dashoffset="gaugeOffset" />
              </svg>
              <div class="hc-gauge-center">
                <div class="hc-gauge-num" :style="{ color: score_theme.color }">{{ total_score }}<small>分</small></div>
              </div>
            </div>

            <div class="hc-hero-info">
              <div class="hc-hero-top">
                <span class="hc-score-tag" :style="{ background: score_theme.color }">
                  <Icon :type="score_theme.key === 'safe' ? 'md-checkmark-circle' : 'md-warning'" size="13" />
                  {{ score_theme.text }}
                </span>
                <span class="hc-hero-title">综合健康度</span>
              </div>
              <div class="hc-hero-desc">
                <Icon type="md-alert" size="15" :color="score_theme.color" />
                <span v-html="grade_desc"></span>
              </div>
            </div>
          </div>

          <!-- 在职人数 -->
          <div class="hc-people">
            <div class="hc-people-hd">
              <span class="hc-people-ic">
                <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="#fff" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="9" cy="8" r="3.2" />
                  <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
                  <path d="M16 5.5a3 3 0 0 1 0 5.8M17.5 20a5.5 5.5 0 0 0-2-4.2" />
                </svg>
              </span>
              <span class="hc-people-title">【{{ report.project_name || '—' }}】在职人数</span>
            </div>
            <div class="hc-people-nums">
              <div class="hc-people-item">
                <div class="n">{{ entry_count === null ? '--' : entry_count }}<em>人</em></div>
                <div class="k">总在职</div>
              </div>
              <div class="hc-people-item">
                <div class="n">{{ changqi_count === null ? '--' : changqi_count }}<em>人</em></div>
                <div class="k">长期工</div>
              </div>
              <div class="hc-people-item">
                <div class="n">{{ duanqi_count === null ? '--' : duanqi_count }}<em>人</em></div>
                <div class="k">短期工</div>
              </div>
            </div>
          </div>
        </div>

        <!-- 体检明细 -->
        <div class="hc-sec-title">体检明细 <span class="hc-sub">按维度展开 · 点击右侧「去处理」直达对应模块</span></div>
        <div class="hc-cards">
          <div class="hc-card" v-for="d in report.dimensions" :key="d.key">
            <div class="hc-card-hd">
              <div class="hc-ic" :style="{ background: d.bg }">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" :stroke="d.color" stroke-width="1.9"
                  stroke-linecap="round" stroke-linejoin="round" v-html="d.svg"></svg>
              </div>
              <div class="tt">
                <div class="n">
                  {{ d.name }}
                  <span class="hc-rule-btn" :class="{ active: open_rule_key === d.key }" @click="toggle_rule(d.key)">
                    <Icon type="md-information-circle" size="13" /> 规则
                  </span>
                </div>
                <div class="d">{{ d.desc }}</div>
              </div>
              <div class="hc-card-score">
                <div class="s" :style="{ color: score_color(d.score) }">{{ d.score }}<em>分</em></div>
              </div>
            </div>
            <div class="hc-rule-note" v-if="open_rule_key === d.key">{{ d.note }}</div>
            <!-- <div class="hc-card-mini">
              <span :style="{ width: d.score + '%', background: score_color(d.score) }"></span>
            </div> -->
            <div class="hc-rows">
              <div class="hc-row" v-for="(it, idx) in d.items" :key="idx">
                <div class="hc-st-dot" :style="{ background: levelBg[it.level] }">
                  <Icon :type="levelIcon[it.level]" :color="levelColor[it.level]" size="11" />
                </div>
                <div class="lbl">{{ it.label }}</div>
                <div class="val">{{ it.value }}</div>
                <span class="hc-tag" :class="it.level">{{ it.status }}</span>
                <span class="hc-act" v-if="it.action" @click="handle_action(d, it)">去处理 ›</span>
              </div>
            </div>
          </div>
        </div>

        <div class="hc-footer">
          <div class="hc-legend">
            <span><i style="background:#19be6b"></i>正常</span>
            <span><i style="background:#ff9900"></i>预警</span>
            <span><i style="background:#ed4014"></i>风险</span>
          </div>
          <div>报告生成于 {{ report.update_time }}</div>
        </div>
      </div>
    </div>

    <Drawer title="身份证照片完善" :closable="true" v-model="idcard_image_drawer_show" width="600">
      <div>
        <Table size="small" :columns="idcard_image_table_colunms" :data="idcard_image_table_data">
          <template #action="{ row }">
            <span class="hc-drawer-act" @click="go_roster_perfect(row)">去完善 ›</span>
          </template>
        </Table>
      </div>
    </Drawer>

    <Drawer title="姓名身份证号实名完善" :closable="true" v-model="cert_drawer_show" width="600">
      <div>
        <Table size="small" :columns="cert_table_colunms" :data="cert_table_data">
          <template #action="{ row }">
            <span class="hc-drawer-act" @click="go_roster_perfect(row)">去完善 ›</span>
          </template>
        </Table>
      </div>
    </Drawer>

    <Drawer title="在职员工未签署合同" :closable="true" v-model="contract_unsigned_drawer_show" width="810">
      <div>
        <Table size="small" :columns="contract_unsigned_table_colunms" :data="contract_unsigned_table_data">
          <template #action="{ row }">
            <span class="hc-drawer-act" @click="go_contract_sign(row)">去签署</span>
            <span class="hc-drawer-act hc-drawer-act-gap" @click="go_contract_detail(row)">查看详情</span>
          </template>
        </Table>
      </div>
    </Drawer>

    <Drawer title="15天内合同到期" :closable="true" v-model="approaching_drawer_show" width="900">
      <div>
        <Table size="small" :columns="approaching_table_colunms" :data="approaching_table_data">
          <template #action="{ row }">
            <span class="hc-drawer-act" @click="go_contract_sign(row)">去签署</span>
            <span class="hc-drawer-act hc-drawer-act-gap" @click="go_contract_detail(row)">查看详情</span>
          </template>
        </Table>
      </div>
    </Drawer>

    <Drawer title="在职员工未参保" :closable="true" v-model="weican_drawer_show" width="900">
      <div>
        <Table size="small" :columns="weican_table_colunms" :data="weican_table_data">
          <template #action="{ row }">
            <span class="hc-drawer-act" @click="go_insurance_create(row)">去参保</span>
            <span class="hc-drawer-act hc-drawer-act-gap" @click="go_insurance_detail(row)">查看详情</span>
          </template>
        </Table>
      </div>
    </Drawer>

    <Drawer title="离职员工未退保" :closable="true" v-model="weitui_drawer_show" width="700">
      <div>
        <Table size="small" :columns="weitui_table_colunms" :data="weitui_table_data">
          <template #action="{ row }">
            <span class="hc-drawer-act" @click="go_insurance_create(row)">去退保</span>
            <span class="hc-drawer-act hc-drawer-act-gap" @click="go_insurance_detail(row)">查看详情</span>
          </template>
        </Table>
      </div>
    </Drawer>
  </div>
</template>

<script>
import { http, open_new_page } from "@/utils/http.js";

export default {
  name: "health_check",
  emits: ["action"],
  data() {
    return {
      show: false,
      phase: "checking", // checking=体检动画中, report=展示报告
      project_id: "", // 当前体检的项目
      scan_token: 0, // 体检流程令牌，用于丢弃中途被关闭/重开的结果
      scan_percent: 0, // 体检进度 0~100（展示值，平滑逼近 target）
      scan_target: 0, // 体检进度目标值（按已完成项实时计算）
      step_status: {}, // 各维度检测状态：pending / loading / done
      smoothTimer: null, // 进度平滑定时器
      finishTimer: null, // 出报告前的停顿定时器
      open_rule_key: "", // 当前展开扣分规则的维度 key
      entry_count: null, // 当前项目总在职人数（取自员工信息接口）
      changqi_count: null, // 长期工在职人数
      duanqi_count: null, // 短期工在职人数
      gaugeOffset: 351.86, // 初始为空环，打开后动画到目标分
      levelBg: { ok: "#e8f8ef", warn: "#fff6e8", err: "#fdeceb" },
      levelColor: { ok: "#19be6b", warn: "#ff9900", err: "#ed4014" },
      levelIcon: { ok: "md-checkmark-circle", warn: "md-alert", err: "md-close-circle" },

      // 维度定义（名称/图标/扣分规则为配置；分数与明细均由接口返回）
      report: {
        project_name: "",
        update_time: "",
        dimensions: [
          {
            key: "personnel",
            name: "项目在职员工信息检测",
            desc: "身份证 / 实名 / 联系方式",
            note: "扣分规则：共100分，身份证未实名 每1人扣10（单项最多50）；身份证照片不完善 每1人扣10（单项最多50）",
            color: "#0466dd",
            bg: "#eaf2fe",
            svg: '<circle cx="9" cy="8" r="3.2"/><path d="M3.5 20a5.5 5.5 0 0 1 11 0"/><path d="M16 11h5M18.5 8.5v5"/>',
            score: 0,
            items: []
          },
          {
            key: "contract",
            name: "项目合同检测",
            desc: "劳动合同 / 电子归档",
            note: "扣分规则：共100分，每1人未签署扣10分(单项最多90分)；每个15天内到期合同扣1分(单项最多10分)",
            color: "#ed4014",
            bg: "#fdeceb",
            svg: '<path d="M8 3h8l4 4v14H4V3z"/><path d="M9 12h6M9 16h6"/><path d="M15 3v5h5"/>',
            score: 0,
            items: []
          },
          {
            key: "insurance",
            name: "项目保险参保检测",
            desc: "五险 / 单工伤 / 商保",
            note: "扣分规则：共100分，每1人未参保扣10分(单项最多50)；每1人未退保扣10分(单项最多50)",
            color: "#ff9900",
            bg: "#fff6e8",
            svg: '<path d="M12 3 4 6v6c0 5 3.4 8.3 8 9 4.6-.7 8-4 8-9V6z"/><path d="M9.5 12.5 11 14l3.5-4"/>',
            score: 0,
            items: []
          },
          {
            key: "project",
            name: "项目资料完善检测",
            desc: "主体 / 介绍 / 岗位 / 需求",
            note: "共100分，均分，未完善则扣对应分数",
            color: "#19be6b",
            bg: "#e8f8ef",
            svg: '<path d="M4 5h16v14H4z"/><path d="M4 9h16M9 9v10"/>',
            score: 0,
            items: []
          }
        ]
      },

      idcard_image_drawer_show: false,
      idcard_image_table_colunms: [
        {
          title: '序号',
          type: 'index',
          width: 60
        },
        {
          title: '姓名',
          key: 'name'
        },
        {
          title: '工作类型',
          key: 'work_status'
        },
        {
          title: '说明',
          key: 'remarks'
        },
        {
          title: '操作',
          key: 'action',
          slot: 'action'
        }],
      idcard_image_table_data: [],

      cert_drawer_show: false,
      cert_table_colunms: [
        {
          title: '序号',
          type: 'index',
          width: 60
        },
        {
          title: '姓名',
          key: 'name'
        },
        {
          title: '工作类型',
          key: 'work_status'
        },
        {
          title: '说明',
          key: 'remarks'
        },
        {
          title: '操作',
          key: 'action',
          slot: 'action'
        }],
      cert_table_data: [],

      contract_unsigned_drawer_show: false,
      contract_unsigned_table_colunms: [
        {
          title: '序号',
          type: 'index',
          width: 60
        },
        {
          title: '姓名',
          key: 'name',
          width: 110
        },
        {
          title: '身份证号',
          key: 'idcard',
          width: 180
        },
        {
          title: '工作类型',
          key: 'work_status',
          width: 100
        },
        {
          title: '入职时间',
          key: 'entry_time',
          width: 140
        },

        {
          title: '操作',
          key: 'action',
          slot: 'action',
          width: 180
        }],
      contract_unsigned_table_data: [],

      approaching_drawer_show: false,

      approaching_table_colunms: [
        {
          title: '序号',
          type: 'index',
          width: 60
        },
        {
          title: '姓名',
          key: 'name',
          width: 110
        },
        {
          title: '身份证号',
          key: 'idcard',
          width: 180
        },
        {
          title: '工作类型',
          key: 'work_status',
          width: 100
        },
        {
          title: '入职时间',
          key: 'entry_time',
          width: 140
        },
        {
          title: '到期时间',
          key: 'lately_time_fmt',
          width: 120
        },
        {
          title: '操作',
          key: 'action',
          slot: 'action',
          width: 180
        }],
      approaching_table_data: [],

      weican_drawer_show: false,
      weican_table_colunms: [
        {
          title: '序号',
          type: 'index',
          width: 60
        },
        {
          title: '姓名',
          key: 'name',
          width: 110
        },
        {
          title: '身份证号',
          key: 'idcard',
          width: 180
        },
        {
          title: '工作类型',
          key: 'work_status',
          width: 100
        },
        {
          title: '操作',
          key: 'action',
          slot: 'action',
          width: 160
        }],
      weican_table_data: [],

      weitui_drawer_show: false,
      weitui_table_colunms: [
        {
          title: '序号',
          type: 'index',
          width: 60
        },
        {
          title: '姓名',
          key: 'name',
          width: 110
        },
        {
          title: '身份证号',
          key: 'idcard',
          width: 180
        },
        {
          title: '工作类型',
          key: 'work_status',
          width: 100
        },
        {
          title: '操作',
          key: 'action',
          slot: 'action',
          width: 160
        }],
      weitui_table_data: []
    };
  },
  computed: {
    gaugeC() {
      return 2 * Math.PI * 56;
    },
    // 总分 = 各体检维度得分的平均值
    total_score() {
      const dims = this.report.dimensions;
      if (!dims.length) return 0;
      const sum = dims.reduce((acc, d) => acc + (Number(d.score) || 0), 0);
      return Math.round((sum / dims.length) * 10) / 10; // 保留 1 位小数
    },
    // 结论文案：满分建议保持，未满分提醒尽快处理
    grade_desc() {
      if (this.total_score >= 100) return "项目各项指标均已达标，建议继续保持！";
      return "存在待完善项，请尽快去处理！";
    },
    // 总分对应的主题（颜色/文案），分数越低越"紧急"
    score_theme() {
      const s = Number(this.total_score) || 0;
      if (s < 75) {
        return {
          key: "danger",
          text: "风险",
          color: "#ed4014",
          color2: "#ff7a45",
          bg: "linear-gradient(135deg, #fff1ee 0%, #ffe2da 55%, #ffffff 100%)",
          border: "#f7bcae",
          glow: "rgba(237, 64, 20, 0.28)"
        };
      }
      if (s < 90) {
        return {
          key: "warning",
          text: "需关注",
          color: "#ff7a00",
          color2: "#ffb340",
          bg: "linear-gradient(135deg, #fff7ea 0%, #ffedd2 55%, #ffffff 100%)",
          border: "#f6cf9c",
          glow: "rgba(255, 122, 0, 0.26)"
        };
      }
      return {
        key: "safe",
        text: "良好",
        color: "#19be6b",
        color2: "#2db7f5",
        bg: "linear-gradient(135deg, #eefaf3 0%, #e6f4ff 55%, #ffffff 100%)",
        border: "#bfe9d3",
        glow: "rgba(25, 190, 107, 0.24)"
      };
    },
    // 当前正在检测的维度名称（可能多个，实时反映接口进度）
    scan_loading_names() {
      return this.report.dimensions
        .filter((d) => this.step_status[d.key] === "loading")
        .map((d) => d.name);
    }
  },
  methods: {
    // 供父组件调用：this.$refs.health_check.open(project_item)
    open(item) {
      this.project_id = (item && item.project_id) || "";
      if (item && item.project_name) {
        this.report.project_name = item.project_name;
      }
      this.report.update_time = this.now_str();
      this.show = true;
      this.run_check();
    },

    // 体检流程：四个维度按各接口真实返回时间推进，全部完成才展示报告
    async run_check() {
      const token = ++this.scan_token;
      this.clear_scan_timer(); // 清理上一次可能残留的定时器
      this.phase = "checking";
      this.gaugeOffset = this.gaugeC; // 报告环形仪表先置空
      this.scan_percent = 0;
      this.scan_target = 0;
      this.open_rule_key = "";
      this.entry_count = null;
      this.changqi_count = null;
      this.duanqi_count = null;
      this.idcard_image_table_data = [];
      this.idcard_image_drawer_show = false;
      this.cert_table_data = [];
      this.cert_drawer_show = false;
      this.contract_unsigned_table_data = [];
      this.contract_unsigned_drawer_show = false;
      this.approaching_table_data = [];
      this.approaching_drawer_show = false;
      this.weican_table_data = [];
      this.weican_drawer_show = false;
      this.weitui_table_data = [];
      this.weitui_drawer_show = false;
      this.init_steps();
      this.start_smooth();

      await Promise.all([
        this.fetch_personnel(token), // 在职员工信息完善度（真实接口）
        this.fetch_contract(token), // 合同签署（真实接口）
        this.fetch_insurance(token), // 保险参保（真实接口）
        this.fetch_project(token) // 项目资料完善度（真实接口）
      ]);

      if (token !== this.scan_token) return; // 期间被关闭/重开，丢弃本次结果
      this.stop_smooth();
      this.scan_target = 100;
      this.scan_percent = 100;
      // 体检完成，自动保存本次总分
      this.save_score(this.total_score);
      this.finishTimer = setTimeout(() => this.enter_report(), 350); // 稍作停顿再出报告
    },

    // 保存本次体检总分到后台
    save_score(score) {
      if (!this.project_id) return;
      http(
        "post",
        process.env.VUE_APP_HOST + "/my_project/detail/save_health_score",
        { project_id: this.project_id, score: score },
        true
      )
        .then((res) => {
          if (res && res.code == 0) {
            console.log("体检分数已保存", score);
          }
        })
        .catch((err) => {
          console.log(err, "体检分数保存失败");
        });
    },

    // 初始化各维度状态为待检测
    init_steps() {
      const status = {};
      this.report.dimensions.forEach((d) => {
        status[d.key] = "pending";
      });
      this.step_status = status;
    },

    // 更新某维度状态，并同步进度目标值
    set_step(key, status) {
      this.step_status[key] = status;
      this.scan_target = this.calc_target();
    },

    // 目标进度 = 已完成数量 / 总数
    calc_target() {
      const keys = this.report.dimensions.map((d) => d.key);
      const total = keys.length || 1;
      const done = keys.filter((k) => this.step_status[k] === "done").length;
      return (done / total) * 100;
    },

    // 进度条平滑逼近目标值
    start_smooth() {
      this.stop_smooth();
      this.smoothTimer = setInterval(() => {
        const diff = this.scan_target - this.scan_percent;
        this.scan_percent = Math.abs(diff) < 0.5 ? this.scan_target : this.scan_percent + diff * 0.2;
      }, 30);
    },

    stop_smooth() {
      if (this.smoothTimer) {
        clearInterval(this.smoothTimer);
        this.smoothTimer = null;
      }
    },

    // 展示报告并播放环形仪表动画
    enter_report() {
      this.phase = "report";
      this.gaugeOffset = this.gaugeC;
      this.$nextTick(() => {
        setTimeout(() => {
          this.gaugeOffset = this.gaugeC * (1 - this.total_score / 100);
        }, 80);
      });
    },

    // 合同数据（真实接口），返回即完成该维度
    fetch_contract(token) {
      if (!this.project_id) {
        this.set_step("contract", "done");
        return Promise.resolve();
      }
      this.set_step("contract", "loading");
      const that = this;
      return http(
        "post",
        process.env.VUE_APP_HOST + "/my_project/project_detail/contract/info",
        { project_id: this.project_id },
        true
      )
        .then((res) => {
          if (token !== this.scan_token) return;
          if (res && res.code == 0) {
            that.apply_contract_data(res.data || {});
            // 在职员工未签署名单
            that.contract_unsigned_table_data =
              (res.data && res.data.contract_dict && res.data.contract_dict.entry_untreated_list) || [];
            // 15天内合同到期名单
            that.approaching_table_data =
              (res.data && res.data.contract_dict && res.data.contract_dict.approaching_expiration_data) ||
              (res.data && res.data.approaching_expiration_data) || [];
          }
        })
        .catch((err) => {
          console.log(err, "合同数据请求出错");
        })
        .then(() => {
          if (token === this.scan_token) that.set_step("contract", "done");
        });
    },

    // 用合同数据填充「合同签署」维度（已签署 / 未签署 / 15天内到期）
    apply_contract_data(data) {
      const cd = data.contract_dict || {};
      const sign_count = cd.entry_sign_count || 0;
      const unsigned_count = cd.entry_untreated_count || 0;
      const approaching_count = cd.approaching_expiration || 0; // 15天内到期合同

      // 当前项目在职人数（员工信息接口未返回时兜底）
      if (this.entry_count === null) {
        this.entry_count = Number(cd.entry_count) || 0;
      }

      let exam_score = data.exam_score;
      if (exam_score === undefined || exam_score === null) exam_score = cd.exam_score;
      if (exam_score === undefined || exam_score === null || isNaN(exam_score)) {
        exam_score = Math.max(0, 100 - unsigned_count * 10 - approaching_count * 1); // 后端未给分时兜底
      }
      exam_score = Number(exam_score);

      // 在职员工总数（直接取接口返回的 total_entry_people_count）
      let total_entry = cd.total_entry_people_count;
      if (total_entry === undefined || total_entry === null) total_entry = data.total_entry_people_count;
      total_entry = Number(total_entry) || 0;

      const dim = this.report.dimensions.find((d) => d.key === "contract");
      if (!dim) return;
      dim.score = exam_score;
      dim.desc = "劳动合同 / 签署情况";
      dim.items = [
        {
          key: "signed",
          label: "在职员工签署",
          value: sign_count + '人' + "/共" + total_entry + '人',
          status: unsigned_count > 0 ? "未签" + unsigned_count + "人" : "正常",
          level: unsigned_count > 0 ? "err" : "ok",
          action: unsigned_count > 0
        },
        {
          key: "expiring",
          label: "15天内合同到期",
          value: approaching_count + " 人",
          status: approaching_count > 0 ? "异常" : "正常",
          level: approaching_count > 0 ? "err" : "ok",
          action: approaching_count > 0
        }
      ];
    },

    // 保险数据（真实接口），返回即完成该维度
    fetch_insurance(token) {
      if (!this.project_id) {
        this.set_step("insurance", "done");
        return Promise.resolve();
      }
      this.set_step("insurance", "loading");
      const that = this;
      return http(
        "post",
        process.env.VUE_APP_HOST + "/my_project/project_detail/insurance_data/cbzz_info",
        { project_id: this.project_id },
        true
      )
        .then((res) => {
          if (token !== this.scan_token) return;
          if (res && res.code == 0) {
            that.apply_insurance_data(res.data || {});
            // 在职员工未参保名单
            that.weican_table_data = (res.data && res.data.zaizhi_weican_data) || [];
            // 离职员工未退保名单
            that.weitui_table_data = (res.data && res.data.lizhi_weitui_data) || [];
          }
        })
        .catch((err) => {
          console.log(err, "保险数据请求出错");
        })
        .then(() => {
          if (token === this.scan_token) that.set_step("insurance", "done");
        });
    },

    // 用保险数据填充「保险参保」维度
    apply_insurance_data(data) {
      const cbzz = data.cbzz_info || {};
      const weican_count = data.zaizhi_weican_count || 0; // 
      const weitui_count = data.lizhi_weitui_count || 0; // 离职未退保
      const zaizhi_total_count = Number(data.zaizhi_total_count) || 0; // 在职总人数
      const lizhi_total_count = Number(data.lizhi_total_count) || 0; // 离职总人数

      // 险种覆盖
      const types = [];
      if ((cbzz.wuxian_be_insured_list || []).length) types.push("五险");
      if ((cbzz.gongshang_be_insured_list || []).length) types.push("单工伤");
      if ((cbzz.gongjijin_be_insured_list || []).length) types.push("公积金");
      if ((cbzz.business_be_insured_list || []).length) types.push("商业险");

      let exam_score = data.exam_score;
      if (exam_score === undefined || exam_score === null || isNaN(exam_score)) {
        // 后端未给分时兜底：在职未参保、离职未退保各 50 分，每少一人扣 10 分
        exam_score = Math.max(0, 50 - weican_count * 10) + Math.max(0, 50 - weitui_count * 10);
      }
      exam_score = Number(exam_score);

      const dim = this.report.dimensions.find((d) => d.key === "insurance");
      if (!dim) return;
      dim.score = exam_score;
      dim.desc = "五险 / 单工伤 / 商保";
      dim.items = [
        {
          key: "not_insured",
          label: "在职员工参保",
          value: Math.max(0, zaizhi_total_count - weican_count) + "人/共" + zaizhi_total_count + "人",
          status: weican_count > 0 ? "未参保" + weican_count + "人" : "正常",
          level: weican_count > 0 ? "err" : "ok",
          action: weican_count > 0
        },
        {
          key: "not_refund",
          label: "离职员工退保",
          value: Math.max(0, lizhi_total_count - weitui_count) + "人/共" + lizhi_total_count + "人",
          status: weitui_count > 0 ? "未退保" + weitui_count + "人" : "正常",
          level: weitui_count > 0 ? "err" : "ok",
          action: weitui_count > 0
        },
        // {
        //   label: "险种配置覆盖",
        //   value: types.length ? types.join(" / ") : "未配置",
        //   status: types.length ? "正常" : "未配置",
        //   level: types.length ? "ok" : "warn",
        //   action: false
        // }
      ];
    },

    // 在职员工信息完善度（真实接口），返回即完成该维度
    fetch_personnel(token) {
      if (!this.project_id) {
        this.set_step("personnel", "done");
        return Promise.resolve();
      }
      this.set_step("personnel", "loading");
      const that = this;
      return http(
        "post",
        process.env.VUE_APP_HOST + "/my_project/employee_information_check",
        { project_id: this.project_id },
        true
      )
        .then((res) => {
          if (token !== this.scan_token) return;
          if (res && res.code == 0) {
            that.apply_personnel_data(res.data || {});
            // 身份证照片缺失人员列表（兼容字符串数组/对象数组）
            that.idcard_image_table_data = (res.data.not_img_name_list || []).map((it) =>
              typeof it === "string" ? { name: it } : it
            );
            // 未实名人员列表
            that.cert_table_data = (res.data.not_certification_name_list || []).map((it) =>
              typeof it === "string" ? { name: it } : it
            );
          }
        })
        .catch((err) => {
          console.log(err, "员工信息数据请求出错");
        })
        .then(() => {
          if (token === this.scan_token) that.set_step("personnel", "done");
        });
    },

    // 用员工信息数据填充「在职员工信息完善度」维度
    apply_personnel_data(data) {
      const entry_people = Number(data.entry_people_count) || 0; // 总在职人数
      const not_cert = data.not_certification_count || 0; // 未实名认证
      const not_img = data.not_img_count || 0; // 身份证照片缺失
      const not_phone = data.not_phone || 0; // 手机号缺失

      // 在职人数：长期工 / 短期工 / 总在职
      this.entry_count = entry_people;
      this.changqi_count = Number(data.changqi_people_count) || 0;
      this.duanqi_count = Number(data.duanqi_people_count) || 0;

      let score = data.score;
      if (score === undefined || score === null || isNaN(score)) {
        // 后端未给分时兜底：实名40、照片40、手机号20，每少一人扣5分
        score =
          Math.max(0, 40 - not_cert * 5) +
          Math.max(0, 40 - not_img * 5) +
          Math.max(0, 20 - not_phone * 5);
      }
      score = Number(score);

      const dim = this.report.dimensions.find((d) => d.key === "personnel");
      if (!dim) return;
      dim.score = score;
      const cert_ok = Math.max(0, entry_people - not_cert);
      const img_ok = Math.max(0, entry_people - not_img);
      dim.items = [
        {
          key: "certification",
          label: "姓名身份证号实名",
          value: cert_ok + "人/共" + entry_people + "人",
          status: not_cert > 0 ? not_cert + "人未实名" : "正常",
          level: not_cert > 0 ? "err" : "ok",
          action: not_cert > 0
        },
        {
          key: "id_photo",
          label: "身份证照片完善",
          value: img_ok + "人/共" + entry_people + "人",
          status: not_img > 0 ? "缺失" + not_img + '人' : "正常",
          level: not_img > 0 ? "err" : "ok",
          action: not_img > 0
        }
      ];
    },

    // 项目资料完善度（真实接口），返回即完成该维度
    fetch_project(token) {
      if (!this.project_id) {
        this.set_step("project", "done");
        return Promise.resolve();
      }
      this.set_step("project", "loading");
      const that = this;
      return http(
        "post",
        process.env.VUE_APP_HOST + "/my_project/detail/perfect_check",
        { project_id: this.project_id },
        true
      )
        .then((res) => {
          if (token !== this.scan_token) return;
          if (res && res.code == 0) {
            that.apply_project_data(res.data || {});
          }
        })
        .catch((err) => {
          console.log(err, "项目完善度数据请求出错");
        })
        .then(() => {
          if (token === this.scan_token) that.set_step("project", "done");
        });
    },

    // 用完善度数据填充「项目资料完善度」维度（明细取 score_list 的 name）
    apply_project_data(data) {
      const list = data.score_list || [];

      let score = data.total_score;
      if (score === undefined || score === null || isNaN(score)) {
        // 后端未给分时兜底：按通过项占比
        const passed = list.filter((i) => i.status == 1).length;
        score = list.length ? Math.round((passed / list.length) * 100) : 0;
      }
      score = Number(score);

      const dim = this.report.dimensions.find((d) => d.key === "project");
      if (!dim) return;
      dim.score = score;
      dim.items = list.map((it, idx) => ({
        key: it.key || it.code || "project_" + idx,
        label: it.name,
        value: it.status == 1 ? "已完善" : "待完善",
        status: it.status == 1 ? "正常" : "待完善",
        level: it.status == 1 ? "ok" : "err",
        action: it.status != 1,
        raw: it // 保留接口原始数据，供处理事件使用
      }));
    },

    // 当前时间字符串 YYYY-MM-DD HH:mm
    now_str() {
      const d = new Date();
      const p = (n) => (n < 10 ? "0" + n : "" + n);
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
    },

    clear_scan_timer() {
      this.stop_smooth();
      if (this.finishTimer) {
        clearTimeout(this.finishTimer);
        this.finishTimer = null;
      }
    },

    close() {
      this.scan_token++; // 使进行中的体检流程失效
      this.clear_scan_timer();
      this.show = false;
    },
    recheck() {
      this.run_check();
    },
    export_report() {
      this.$Message.info("报告导出功能开发中");
    },
    // 维度分数按绿/橙/红分档：≥90 绿，≥75 橙，其余红
    score_color(score) {
      const s = Number(score) || 0;
      if (s < 75) return "#ed4014";
      if (s < 90) return "#ff9900";
      return "#19be6b";
    },
    // 展开/收起扣分规则
    toggle_rule(key) {
      this.open_rule_key = this.open_rule_key === key ? "" : key;
    },
    /**
     * 「去处理」：按「维度 + 小类」抛给父组件，由父组件决定具体行为
     * 用法：<health_check ref="health_check" @action="on_health_action" />
     * 参数：
     *   dimension      personnel | contract | insurance | project
     *   dimension_name 维度名称
     *   item_key       小类 key，见各维度 items 的 key 字段
     *   item           当前明细（project 维度额外带 raw 原始数据）
     *   project_id / project_name 当前项目
     */
    handle_action(dim, item) {
      // 身份证照片缺失：打开「身份证照片完善」抽屉
      if (item.key === "id_photo") {
        this.idcard_image_drawer_show = true;
        return;
      }
      // 姓名身份证号未实名：打开「实名完善」抽屉
      if (item.key === "certification") {
        this.cert_drawer_show = true;
        return;
      }
      // 在职员工未签合同：打开「未签署合同」抽屉
      if (item.key === "signed") {
        this.contract_unsigned_drawer_show = true;
        return;
      }
      // 15天内合同到期：打开「合同到期」抽屉
      if (item.key === "expiring") {
        this.approaching_drawer_show = true;
        return;
      }
      // 在职员工未参保：打开「未参保」抽屉
      if (item.key === "not_insured") {
        this.weican_drawer_show = true;
        return;
      }
      // 离职员工未退保：打开「未退保」抽屉
      if (item.key === "not_refund") {
        this.weitui_drawer_show = true;
        return;
      }
      // 项目资料完善：项目介绍跳完善页，其余统一提示
      if (dim.key === "project") {
        if ((item.label || "").indexOf("项目介绍") !== -1) {
          open_new_page("/project_introduction?project_id=" + this.project_id);
          return;
        }
        this.$Message.info("请前往项目信息页进行自行更新");
        return;
      }
      this.$emit("action", {
        dimension: dim.key,
        dimension_name: dim.name,
        item_key: item.key,
        item: item,
        project_id: this.project_id,
        project_name: this.report.project_name
      });
    },

    // 抽屉内「去完善」：打开当前项目的在职花名册，并按姓名搜索
    go_roster_perfect(row) {
      const search_value = encodeURIComponent((row && row.name) || "");
      open_new_page(
        "/roster_manage/detail?project_id=" + this.project_id +
        "&job_status=0&project_name=" + (this.report.project_name || "") +
        "&search_type=name&search_value=" + search_value
      );
    },

    // 未签署合同「去签署」：打开电子签创建页
    go_contract_sign(row) {
      open_new_page(
        "https://contract.daxiangsys.com/sign/esign/create?name=" + encodeURIComponent((row && row.name) || "") +
        "&phone=" + encodeURIComponent((row && row.phone) || "") +
        "&idcard=" + encodeURIComponent((row && row.idcard) || "") +
        "&project_id=" + encodeURIComponent(this.project_id) +
        "&interview_status=" + encodeURIComponent(row.work_status) +
        "&interview_id=" + encodeURIComponent(row.interview_id)
      );
    },

    // 未签署合同「查看详情」：打开合同管理详情页
    go_contract_detail(row) {
      open_new_page(
        "https://contract.daxiangsys.com/contract_manage/search/detail?name=" + encodeURIComponent((row && row.name) || "") +
        "&idcard=" + encodeURIComponent((row && row.idcard) || "") +
        "&phone=" + encodeURIComponent((row && row.phone) || "")
      );
    },

    // 参保「去参保」：打开保险创建页，携带当前人员信息
    go_insurance_create(row) {
      const resume_data = encodeURIComponent(
        JSON.stringify([
          {
            name: (row && row.name) || "",
            idcard: (row && row.idcard) || ""
          }
        ])
      );
      open_new_page(
        "https://insurance.daxiangsys.com/create/v2?project_id=" + encodeURIComponent(this.project_id) +
        "&resume_data=" + resume_data
      );
    },

    // 参保「查看详情」：打开保险系统人员详情
    go_insurance_detail(row) {
      open_new_page(
        "https://insurance.daxiangsys.com/info/search/detail/?idcard=" + encodeURIComponent((row && row.idcard) || "") +
        "&project_id=" + encodeURIComponent(this.project_id)
      );
    }
  },
  beforeUnmount() {
    this.clear_scan_timer();
  }
};
</script>

<style scoped>
.hc-mask {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(23, 35, 61, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow: auto;
  padding: 42px 20px;
}

/* ===== 体检动画 ===== */
.hc-scan {
  position: relative;
  z-index: 6;
  width: 420px;
  max-width: 92vw;
  margin: 12vh auto 0;
}

.hc-scan-card {
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 12px 48px rgba(23, 35, 61, 0.28);
  padding: 34px 30px 28px;
  text-align: center;
  animation: hcPop 0.3s cubic-bezier(0.16, 0.84, 0.44, 1) both;
}

/* 心跳图标 + 扩散圈 */
.hc-scan-ring {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  margin: 0 auto 20px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(circle, #eaf2fe 0%, #f7fbff 72%);
}

.hc-scan-ring::before,
.hc-scan-ring::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid rgba(4, 102, 221, 0.45);
  animation: hcPulse 1.8s ease-out infinite;
}

.hc-scan-ring::after {
  animation-delay: 0.9s;
}

@keyframes hcPulse {
  0% {
    transform: scale(0.85);
    opacity: 0.8;
  }

  100% {
    transform: scale(1.65);
    opacity: 0;
  }
}

.hc-heart {
  animation: hcHeart 1.1s ease-in-out infinite;
}

@keyframes hcHeart {

  0%,
  100% {
    transform: scale(1);
  }

  28% {
    transform: scale(1.18);
  }

  46% {
    transform: scale(1);
  }

  62% {
    transform: scale(1.12);
  }
}

.hc-scan-title {
  font-size: 17px;
  font-weight: 700;
  color: #17233d;
  letter-spacing: 0.5px;
}

.hc-scan-sub {
  font-size: 12.5px;
  color: #808695;
  margin-top: 8px;
  height: 18px;
}

/* 进度条 */
.hc-scan-bar {
  height: 8px;
  border-radius: 20px;
  background: #eef1f5;
  overflow: hidden;
  margin: 18px 0 8px;
}

.hc-scan-bar>span {
  display: block;
  height: 100%;
  border-radius: 20px;
  background: linear-gradient(90deg, #0466dd, #2db7f5);
  transition: width 0.12s linear;
  position: relative;
  overflow: hidden;
}

.hc-scan-bar>span::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.55), transparent);
  animation: hcShimmer 1.1s linear infinite;
}

@keyframes hcShimmer {
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(100%);
  }
}

.hc-scan-percent {
  font-size: 13px;
  font-weight: 700;
  color: #0466dd;
  font-variant-numeric: tabular-nums;
}

/* 步骤列表 */
.hc-scan-steps {
  margin-top: 18px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  text-align: left;
}

.hc-scan-step {
  display: flex;
  align-items: center;
  gap: 9px;
  font-size: 13px;
  color: #c5c8ce;
  transition: 0.25s;
}

.hc-scan-step.done {
  color: #515a6e;
}

.hc-scan-step.active {
  color: #0466dd;
  font-weight: 600;
}

.hc-scan-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #e8eaec;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.hc-scan-step.done .hc-scan-dot {
  background: #19be6b;
}

.hc-scan-step.active .hc-scan-dot {
  background: #0466dd;
}

.hc-scan-spin {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  animation: hcSpin 0.7s linear infinite;
}

@keyframes hcSpin {
  to {
    transform: rotate(360deg);
  }
}


.hc-panel {
  width: min(1180px, 100%);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 48px rgba(23, 35, 61, 0.28);
  overflow: hidden;
  animation: hcPop 0.35s cubic-bezier(0.16, 0.84, 0.44, 1) both;
}

@keyframes hcPop {
  from {
    opacity: 0;
    transform: translateY(18px) scale(0.985);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

/* 头部 */
.hc-hd {
  background: linear-gradient(120deg, #0466dd 0%, #2a86f0 55%, #2db7f5 100%);
  color: #fff;
  padding: 20px 26px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  overflow: hidden;
}

.hc-hd::after {
  content: "";
  position: absolute;
  right: -60px;
  top: -80px;
  width: 260px;
  height: 260px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
}

.hc-hd::before {
  content: "";
  position: absolute;
  right: 90px;
  bottom: -90px;
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
}

.hc-hd-l {
  display: flex;
  align-items: center;
  gap: 14px;
  position: relative;
  z-index: 1;
}

.hc-hd-badge {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.28);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.hc-hd-title {
  font-size: 19px;
  font-weight: 700;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.hc-hd-title .hc-ver {
  font-size: 11px;
  font-weight: 400;
  background: rgba(255, 255, 255, 0.2);
  padding: 1px 7px;
  border-radius: 20px;
  letter-spacing: 0;
}

.hc-hd-sub {
  font-size: 12.5px;
  opacity: 0.85;
  margin-top: 5px;
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.hc-hd-sub b {
  font-weight: 500;
}

.hc-hd-r {
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  z-index: 1;
}

.hc-btn {
  border: 1px solid rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-size: 13px;
  padding: 7px 15px;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: 0.18s;
  white-space: nowrap;
}

.hc-btn:hover {
  background: rgba(255, 255, 255, 0.28);
}

.hc-btn-solid {
  background: #fff;
  color: #0466dd;
  border-color: #fff;
  font-weight: 600;
}

.hc-btn-solid:hover {
  background: #eef5ff;
}

.hc-close {
  font-size: 26px;
  color: #fff;
  cursor: pointer;
  margin-left: 4px;
  opacity: 0.9;
}

.hc-close:hover {
  opacity: 1;
}

/* 主体 */
.hc-body {
  padding: 0px 20px 26px 20px;
  max-height: calc(92vh - 110px);
  overflow: auto;
}

/* 总分 + 在职人数 */
.hc-top-row {
  display: flex;
  align-items: stretch;
  gap: 16px;
  margin-top: 16px;
}

/* 总分 */
.hc-hero {
  flex: 0 0 50%;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 24px;
  padding: 10px 26px;
  border: 1px solid #e8eaec;
  border-radius: 18px;
  box-shadow: 0 12px 36px var(--glow);
  will-change: box-shadow;
  animation: hcCardPulse 3s ease-in-out infinite;
}

/* 在职人数卡片 */
.hc-people {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 18px 24px;
  border: 1px solid #d6e4fb;
  border-radius: 18px;
  background: linear-gradient(135deg, #f2f8ff 0%, #e6f0fe 100%);
  box-shadow: 0 8px 24px rgba(4, 102, 221, 0.1);
}

.hc-people-hd {
  display: flex;
  align-items: center;
  gap: 8px;
}

.hc-people-ic {
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background: linear-gradient(135deg, #0466dd, #2db7f5);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.hc-people-title {
  font-size: 13px;
  font-weight: 600;
  color: #17233d;
  letter-spacing: 1px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 总在职 / 长期工 / 短期工 横向铺开 */
.hc-people-nums {
  display: flex;
  align-items: flex-end;
  margin-top: 10px;
}

.hc-people-item {
  flex: 1;
  min-width: 0;
}

.hc-people-item+.hc-people-item {
  border-left: 1px dashed #cfe0f8;
  padding-left: 18px;
}

.hc-people-item .n {
  font-size: 30px;
  font-weight: 800;
  line-height: 1.1;
  color: #17233d;
  letter-spacing: -1px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.hc-people-item .n em {
  font-style: normal;
  font-size: 11px;
  font-weight: 600;
  color: #808695;
  margin-left: 2px;
  letter-spacing: 0;
}

.hc-people-item .k {
  margin-top: 4px;
  font-size: 12px;
  color: #808695;
}

/* 通栏横幅的轻微光晕呼吸（不做缩放，避免溢出） */
@keyframes hcCardPulse {

  0%,
  100% {
    box-shadow: 0 12px 36px var(--glow);
  }

  50% {
    box-shadow: 0 18px 50px var(--glow);
  }
}

/* 等级徽标 */
.hc-score-tag {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  font-weight: 700;
  letter-spacing: 1px;
  color: #fff;
  padding: 3px 14px;
  border-radius: 20px;
  white-space: nowrap;
}

.hc-hero-info {
  flex: none;
}

.hc-hero-top {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.hc-hero-title {
  font-size: 15px;
  font-weight: 700;
  color: #17233d;
  letter-spacing: 2px;
}

.hc-hero-desc {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  line-height: 1.7;
  color: #515a6e;
}

.hc-gauge {
  position: relative;
  width: 140px;
  height: 140px;
  flex-shrink: 0;
}

/* 向外扩散的呼吸光环，吸引注意力 */
.hc-gauge::before,
.hc-gauge::after {
  content: "";
  position: absolute;
  inset: 8px;
  border-radius: 50%;
  border: 2px solid var(--c);
  opacity: 0;
  animation: hcGaugePulse 2.4s ease-out infinite;
}

.hc-gauge::after {
  animation-delay: 1.2s;
}

@keyframes hcGaugePulse {
  0% {
    transform: scale(0.9);
    opacity: 0.45;
  }

  70% {
    opacity: 0;
  }

  100% {
    transform: scale(1.16);
    opacity: 0;
  }
}

.hc-gauge svg {
  transform: rotate(-90deg);
  position: relative;
  z-index: 1;
}

.hc-gauge .hc-track {
  fill: none;
  stroke: #eef1f6;
  stroke-width: 11;
}

.hc-gauge .hc-bar {
  fill: none;
  stroke: url(#hcGaugeGrad);
  stroke-width: 11;
  stroke-linecap: round;
  transition: stroke-dashoffset 1.1s cubic-bezier(0.16, 0.84, 0.44, 1);
}

.hc-gauge-center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.hc-gauge-num {
  font-size: 32px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -1px;
  font-variant-numeric: tabular-nums;
}

.hc-gauge-num small {
  font-size: 12px;
  font-weight: 600;
  color: #808695;
  margin-left: 2px;
  letter-spacing: 0;
}

/* 分段标题 */
.hc-sec-title {
  margin: 26px 0 14px;
  font-size: 14.5px;
  font-weight: 700;
  color: #17233d;
  display: flex;
  align-items: center;
  gap: 8px;
}

.hc-sec-title::before {
  content: "";
  width: 4px;
  height: 15px;
  border-radius: 3px;
  background: #0466dd;
}

.hc-sec-title .hc-sub {
  font-size: 12px;
  color: #808695;
  font-weight: 400;
}

/* 维度卡片 */
.hc-cards {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.hc-card {
  border: 1px solid #e8eaec;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  transition: 0.2s;
}

.hc-card:hover {
  box-shadow: 0 6px 20px rgba(4, 102, 221, 0.09);
  border-color: #cfe1fa;
}

.hc-card-hd {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 15px;
  border-bottom: 1px solid #f1f3f6;
  background: #fcfdff;
}

.hc-ic {
  width: 34px;
  height: 34px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.hc-card-hd .tt {
  flex: 1;
}

.hc-card-hd .tt .n {
  font-size: 13.5px;
  font-weight: 600;
  color: #17233d;
}

.hc-card-hd .tt .d {
  font-size: 11.5px;
  color: #808695;
  margin-top: 2px;
}

.hc-card-score .s {
  font-size: 22px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

.hc-card-score .s em {
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  color: #808695;
}

.hc-card-mini {
  height: 5px;
  border-radius: 20px;
  background: #eef1f5;
  margin: 0 15px;
  overflow: hidden;
  position: relative;
  top: -1px;
}

.hc-card-mini>span {
  display: block;
  height: 100%;
  border-radius: 20px;
}

.hc-rows {
  padding: 6px 15px 12px;
}

/* 标题旁的扣分规则入口 */
.hc-rule-btn {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-left: 8px;
  font-size: 12px;
  font-weight: 400;
  color: #d2d0d0;
  cursor: pointer;
  vertical-align: middle;
}

.hc-rule-btn:hover {
  text-decoration: underline;
}

.hc-rule-btn.active {
  font-weight: 600;
}

/* 展开的扣分规则 */
.hc-rule-note {
  margin: 0 15px 10px;
  padding: 8px 12px;
  background: #f2f7ff;
  border: 1px solid #d6e6ff;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.7;
  color: #515a6e;
}

.hc-row {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 9px 0;
  border-bottom: 1px dashed #eef0f3;
}

.hc-row:last-child {
  border-bottom: none;
}

.hc-st-dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hc-row .lbl {
  flex: 1;
  font-size: 13px;
  color: #515a6e;
}

.hc-row .val {
  font-size: 12.5px;
  color: #808695;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.hc-tag {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 5px;
  font-weight: 500;
  white-space: nowrap;
}

.hc-tag.ok {
  background: #e8f8ef;
  color: #19be6b;
}

.hc-tag.warn {
  background: #fff6e8;
  color: #ff9900;
}

.hc-tag.err {
  background: #fdeceb;
  color: #ed4014;
}

.hc-row .hc-act {
  font-size: 12px;
  color: #0466dd;
  cursor: pointer;
  white-space: nowrap;
}

.hc-row .hc-act:hover {
  color: #0356bd;
  font-weight: 600;
}

/* 抽屉内「去完善」 */
.hc-drawer-act {
  font-size: 12.5px;
  color: #0466dd;
  cursor: pointer;
}

.hc-drawer-act:hover {
  color: #0356bd;
  font-weight: 600;
}

.hc-drawer-act-gap {
  margin-left: 14px;
}

.hc-row:hover {
  background: #fafcff;
  border-radius: 6px;
}

/* 页脚 */
.hc-footer {
  margin-top: 22px;
  padding-top: 15px;
  border-top: 1px solid #e8eaec;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  color: #808695;
}

.hc-footer .hc-legend {
  display: flex;
  gap: 16px;
}

.hc-footer .hc-legend span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.hc-footer .hc-legend i {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
</style>
